import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { api, useAuth } from '../App';
import { useCurrency } from '../contexts/CurrencyContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  TrendingUp,
  TrendingDown,
  ArrowRight,
  Wallet,
  PieChart,
  Activity,
  Bot,
  Store,
  RefreshCw,
  Wifi,
  WifiOff,
  Users
} from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';

export default function Dashboard() {
  const { user, updateUser } = useAuth();
  const { formatCurrency, preferredCurrency, getSymbol } = useCurrency();
  const [portfolio, setPortfolio] = useState(null);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [marketData, setMarketData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  const fetchData = async () => {
    try {
      const [portfolioRes, transactionsRes, marketRes, userRes] = await Promise.allSettled([
        api.get('/portfolio/'),
        api.get('/payments/transactions'),
        api.get('/market/all'),
        api.get('/auth/me')
      ]);
      
      if (portfolioRes.status === 'fulfilled') setPortfolio(portfolioRes.value.data);
      if (transactionsRes.status === 'fulfilled') setRecentTransactions(transactionsRes.value.data.slice(0, 5));
      if (marketRes.status === 'fulfilled') setMarketData(marketRes.value.data.slice(0, 6));
      if (userRes.status === 'fulfilled') updateUser(userRes.value.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
    connectWebSocket();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, []);

  const connectWebSocket = () => {
    const wsUrl = process.env.REACT_APP_BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    
    try {
      wsRef.current = new WebSocket(`${wsUrl}/ws/market`);
      
      wsRef.current.onopen = () => {
        setWsConnected(true);
        console.log('WebSocket connected');
      };
      
      wsRef.current.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'market_update') {
          setMarketData(data.data.slice(0, 6));
        }
      };
      
      wsRef.current.onclose = () => {
        setWsConnected(false);
        console.log('WebSocket disconnected, reconnecting...');
        reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
      };
      
      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        setWsConnected(false);
      };
    } catch (error) {
      console.error('WebSocket connection error:', error);
      setWsConnected(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
    toast.success('Data refreshed');
  };

  const formatPercent = (value) => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value?.toFixed(2) || 0}%`;
  };

  // Mock chart data
  const chartData = [
    { date: 'Mon', value: 1000 },
    { date: 'Tue', value: 1050 },
    { date: 'Wed', value: 1030 },
    { date: 'Thu', value: 1120 },
    { date: 'Fri', value: 1180 },
    { date: 'Sat', value: 1150 },
    { date: 'Sun', value: portfolio?.total_value || 1200 }
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-2 border-[#00E676] border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 
              className="text-2xl md:text-3xl font-bold text-white"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Welcome back, {user?.name?.split(' ')[0]}
            </h1>
            <p className="text-gray-400 mt-1">Here's your portfolio overview</p>
          </div>
          <div className="flex gap-3">
            <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${wsConnected ? 'bg-[#00E676]/20 text-[#00E676]' : 'bg-red-500/20 text-red-400'}`}>
              {(wsConnected || marketData.length > 0) ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
              {(wsConnected || marketData.length > 0) ? 'Live' : 'Offline'}
            </div>
            <Button 
              variant="outline" 
              className="btn-secondary gap-2"
              onClick={handleRefresh}
              disabled={refreshing}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Link to="/deposit">
              <Button className="btn-primary gap-2" data-testid="deposit-btn">
                <Wallet className="w-4 h-4" />
                Deposit
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Cash Balance */}
          <Card className="card-dashboard">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">Cash Balance</span>
                <Wallet className="w-5 h-5 text-[#00E676]" />
              </div>
              <p className="text-2xl font-bold text-white number" data-testid="cash-balance">
                {formatCurrency(user?.balance)}
              </p>
              <p className="text-sm text-gray-500 mt-1">Available to trade</p>
            </CardContent>
          </Card>

          {/* Portfolio Value */}
          <Card className="card-dashboard">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">Portfolio Value</span>
                <PieChart className="w-5 h-5 text-[#6366F1]" />
              </div>
              <p className="text-2xl font-bold text-white number" data-testid="portfolio-value">
                {formatCurrency(portfolio?.total_value)}
              </p>
              <p className={`text-sm mt-1 ${portfolio?.profit_loss_percent >= 0 ? 'text-profit' : 'text-loss'}`}>
                {formatPercent(portfolio?.profit_loss_percent)} all time
              </p>
            </CardContent>
          </Card>

          {/* Total Invested */}
          <Card className="card-dashboard">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">Total Invested</span>
                <Activity className="w-5 h-5 text-[#F7931A]" />
              </div>
              <p className="text-2xl font-bold text-white number" data-testid="total-invested">
                {formatCurrency(portfolio?.total_invested)}
              </p>
              <p className="text-sm text-gray-500 mt-1">In assets</p>
            </CardContent>
          </Card>

          {/* Profit/Loss */}
          <Card className="card-dashboard">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">Total P&L</span>
                {portfolio?.total_profit_loss >= 0 ? (
                  <TrendingUp className="w-5 h-5 text-profit" />
                ) : (
                  <TrendingDown className="w-5 h-5 text-loss" />
                )}
              </div>
              <p className={`text-2xl font-bold number ${portfolio?.total_profit_loss >= 0 ? 'text-profit' : 'text-loss'}`} data-testid="total-pnl">
                {formatCurrency(portfolio?.total_profit_loss)}
              </p>
              <p className={`text-sm mt-1 ${portfolio?.profit_loss_percent >= 0 ? 'text-profit' : 'text-loss'}`}>
                {formatPercent(portfolio?.profit_loss_percent)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Chart */}
          <Card className="card-dashboard lg:col-span-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Portfolio Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#00E676" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#00E676" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="date" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1A1A1A', 
                        border: '1px solid #333',
                        borderRadius: '8px'
                      }}
                      labelStyle={{ color: '#fff' }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="value" 
                      stroke="#00E676" 
                      fillOpacity={1} 
                      fill="url(#colorValue)" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="card-dashboard">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Small Business - LIVE TRADING */}
              <Link to="/business" className="block">
                <div className="p-4 rounded-xl bg-gradient-to-r from-[#00E676]/10 to-[#00E676]/5 border border-[#00E676]/30 hover:border-[#00E676]/50 transition-colors group cursor-pointer" data-testid="quick-business">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#00E676]/20 flex items-center justify-center">
                      <Store className="w-5 h-5 text-[#00E676]" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-white">Small Business</p>
                        <span className="px-2 py-0.5 text-xs rounded-full bg-[#00E676] text-black font-semibold">LIVE</span>
                      </div>
                      <p className="text-sm text-gray-500">Invest real money • Start at $5</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-[#00E676] group-hover:translate-x-1 transition-all" />
                  </div>
                </div>
              </Link>

              {/* Browse Markets - LIVE */}
              <Link to="/live-markets" className="block p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors cursor-pointer" data-testid="quick-markets">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#2962FF]/20 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-[#2962FF]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-white">Browse Markets</p>
                      <span className="px-2 py-0.5 text-xs rounded-full bg-white/10 text-gray-400">VIEW ONLY</span>
                    </div>
                    <p className="text-sm text-gray-500">Stocks, crypto & metals • Real-time prices</p>
                  </div>
                </div>
              </Link>

              {/* AI Trading - LIVE */}
              <Link to="/ai-trading" className="block p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors cursor-pointer" data-testid="quick-ai">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#6366F1]/20 flex items-center justify-center">
                    <Bot className="w-5 h-5 text-[#6366F1]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-white">AI Trading</p>
                      <span className="px-2 py-0.5 text-xs rounded-full bg-white/10 text-gray-400">OFF</span>
                    </div>
                    <p className="text-sm text-gray-500">Turned off while real-money trading is limited to small businesses</p>
                  </div>
                </div>
              </Link>

              {/* Social Trading - NEW */}
              <Link to="/social" className="block p-4 rounded-xl bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 hover:border-purple-500/50 transition-colors cursor-pointer" data-testid="quick-social">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <Users className="w-5 h-5 text-purple-400" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-white">Social Trading</p>
                      <span className="px-2 py-0.5 text-xs rounded-full bg-purple-500 text-white font-semibold">NEW</span>
                    </div>
                    <p className="text-sm text-gray-500">Follow traders • Copy their trades</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-purple-400" />
                </div>
              </Link>
            </CardContent>
          </Card>
        </div>

        {/* Bottom Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Holdings */}
          <Card className="card-dashboard">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-lg text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Your Holdings
              </CardTitle>
              <Link to="/markets">
                <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                  View All <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {portfolio?.assets?.length > 0 ? (
                <div className="space-y-3">
                  {portfolio.assets.slice(0, 5).map((asset, i) => (
                    <div 
                      key={i} 
                      className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          asset.asset_type === 'crypto' ? 'bg-[#F7931A]/20 text-[#F7931A]' :
                          asset.asset_type === 'metal' ? 'bg-yellow-500/20 text-yellow-500' :
                          'bg-[#2962FF]/20 text-[#2962FF]'
                        }`}>
                          <span className="text-xs font-bold">{asset.symbol}</span>
                        </div>
                        <div>
                          <p className="font-medium text-white">{asset.symbol}</p>
                          <p className="text-sm text-gray-500">{asset.quantity} units</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-white number">{formatCurrency(asset.total_value)}</p>
                        <p className={`text-sm ${asset.profit_loss >= 0 ? 'text-profit' : 'text-loss'}`}>
                          {formatPercent(asset.profit_loss_percent)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <PieChart className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400">No holdings yet</p>
                  <Link to="/markets">
                    <Button className="btn-primary mt-4" size="sm">
                      Start Trading
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Transactions */}
          <Card className="card-dashboard">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-lg text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Recent Activity
              </CardTitle>
              <Link to="/transactions">
                <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                  View All <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {recentTransactions.length > 0 ? (
                <div className="space-y-3">
                  {recentTransactions.map((txn, i) => (
                    <div 
                      key={i}
                      className="flex items-center justify-between p-3 rounded-lg bg-white/5"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          txn.type === 'deposit' ? 'bg-[#00E676]/20 text-[#00E676]' :
                          txn.type === 'withdraw' ? 'bg-[#FF3B30]/20 text-[#FF3B30]' :
                          txn.type === 'buy' ? 'bg-[#2962FF]/20 text-[#2962FF]' :
                          'bg-[#F7931A]/20 text-[#F7931A]'
                        }`}>
                          {txn.type === 'deposit' && <TrendingUp className="w-5 h-5" />}
                          {txn.type === 'withdraw' && <TrendingDown className="w-5 h-5" />}
                          {txn.type === 'buy' && <TrendingUp className="w-5 h-5" />}
                          {txn.type === 'sell' && <TrendingDown className="w-5 h-5" />}
                        </div>
                        <div>
                          <p className="font-medium text-white capitalize">{txn.type}</p>
                          <p className="text-sm text-gray-500 truncate max-w-[200px]">{txn.description}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-medium number ${
                          ['deposit', 'sell'].includes(txn.type) ? 'text-profit' : 'text-loss'
                        }`}>
                          {['deposit', 'sell'].includes(txn.type) ? '+' : '-'}
                          {formatCurrency(txn.amount)}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(txn.timestamp).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Activity className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400">No transactions yet</p>
                  <Link to="/deposit">
                    <Button className="btn-primary mt-4" size="sm">
                      Make a Deposit
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Market Overview */}
        <Card className="card-dashboard">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-lg text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Market Overview
            </CardTitle>
            <Link to="/markets">
              <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                All Markets <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {marketData.map((asset, i) => (
                <div 
                  key={i}
                  className="p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      asset.asset_type === 'crypto' ? 'bg-[#F7931A]/20 text-[#F7931A]' :
                      asset.asset_type === 'metal' ? 'bg-yellow-500/20 text-yellow-500' :
                      'bg-[#2962FF]/20 text-[#2962FF]'
                    }`}>
                      {asset.asset_type}
                    </span>
                  </div>
                  <p className="font-semibold text-white">{asset.symbol}</p>
                  <p className="text-lg font-bold text-white number mt-1">
                    ${asset.price.toLocaleString()}
                  </p>
                  <p className={`text-sm ${asset.change_percent >= 0 ? 'text-profit' : 'text-loss'}`}>
                    {formatPercent(asset.change_percent)}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
